import { useState, useCallback } from 'react';
import * as plansService from '@/services/plans.service';
import mixpanel from '@/utils/mixpanel';
import useModalStore from '@/stores/modal/modalStore';
import useToast from '@/hooks/useToast';

/** Backend tier names that correspond to each tier group for checkout */
const TARGET_TIER_NAMES = {
  starter: ['starter', 'entry'],
  advanced: ['advanced', 'premium', 'pro', 'professional'],
  pro: ['business', 'enterprise', 'ultra'],
};

/**
 * Find the first tier id from tiers data that matches the target tier group.
 * @param {object} tiersData - Response from getTiers() e.g. { monthly: [{ id, name }], yearly: [...] }
 * @param {string} targetGroup - 'starter' | 'advanced' | 'pro'
 * @returns {string|null} Tier id or null
 */
function findTierIdForTarget(tiersData, targetGroup) {
  const names = TARGET_TIER_NAMES[targetGroup];
  if (!names || !tiersData) return null;

  const period = tiersData.monthly ? 'monthly' : tiersData.yearly ? 'yearly' : Object.keys(tiersData)[0];
  const list = tiersData[period];
  if (!Array.isArray(list)) return null;

  const match = list.find((t) => names.includes(String(t.name).toLowerCase()));
  return match?.id ?? null;
}

function getFailureTrackingProps(targetGroup, errorMessage) {
  const state = useModalStore.getState();
  return {
    version: 'pricing-modal-v2',
    modal_variant: state.modalVariant,
    current_tier: state.upgradeContext?.currentTier,
    target_tier: state.upgradeContext?.targetTier,
    feature_name: state.upgradeContext?.feature || null,
    plan_shown: targetGroup,
    error_message: errorMessage,
  };
}

/**
 * Hook to start checkout for a given target tier group (starter, advanced, pro).
 * @param {string} targetGroup - 'starter' | 'advanced' | 'pro'
 * @returns {{ startCheckout: () => Promise<void>, isLoading: boolean }}
 */
export function useUpgradeCheckout(targetGroup) {
  const [isLoading, setIsLoading] = useState(false);
  const { addErrorToast } = useToast();

  const startCheckout = useCallback(async () => {
    if (!targetGroup) return;
    setIsLoading(true);
    try {
      const { data: tiers } = await plansService.getTiers();
      const tierId = findTierIdForTarget(tiers, targetGroup);
      if (!tierId) {
        const msg = 'Plan checkout is not available yet. Please try again shortly.';
        mixpanel.track('Upgrade Path Modal CTA Failed', getFailureTrackingProps(targetGroup, msg));
        addErrorToast({ message: msg });
        setIsLoading(false);
        return;
      }
      const response = await plansService.getCheckoutSession(tierId);
      const checkoutUrl = response?.data?.checkout_session?.url;
      if (!checkoutUrl) {
        const msg = 'Failed to start checkout. Please try again.';
        mixpanel.track('Upgrade Path Modal CTA Failed', getFailureTrackingProps(targetGroup, msg));
        addErrorToast({ message: msg });
        setIsLoading(false);
        return;
      }
      window.location.assign(checkoutUrl);
    } catch (err) {
      const raw =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message;
      const message =
        typeof raw === 'string' ? raw : 'Failed to start checkout. Please try again.';
      mixpanel.track('Upgrade Path Modal CTA Failed', getFailureTrackingProps(targetGroup, message));
      addErrorToast({ message });
      console.error('Upgrade checkout error:', err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetGroup]);

  return { startCheckout, isLoading };
}
